import { useFeatureValue } from '@shared/core/featureFlags';
import { addError } from '@shared/utils/logger';
import { useCallback } from 'react';
import { useUserPublicConsentKeyForEventQuery } from '@graphql/generated';
import { useEventInfo } from '@shared/utils/eventInfo';
import { UrlObject } from 'url';

export type WebsiteInterceptExternalHotelLinksPayload = ReadonlyArray<HotelFilter>;
export interface HotelFilter {
  hostname: ReadonlyArray<string>;
  matchingLogic: ReadonlyArray<
    Readonly<{
      pathname?: string;
      search?: string;
      result: boolean;
    }>
  >;
}

const tryCreateRegex = (regexString?: string): RegExp | null => {
  try {
    if (regexString) {
      return new RegExp(regexString, 'i');
    }
  } catch (e) {
    if (e instanceof Error) {
      addError('regexParseError', { type: 'parse_error', regexString, error: e });
    } else {
      addError('regexParseError', { type: 'unknown_error', regexString, error: e });
    }
  }
  return null;
};

const parseUrl = (url: string): UrlObject | null => {
  if (!url) {
    return null;
  }
  try {
    return new URL(url);
  } catch (e) {
    addError('urlParseError', { type: 'parse_error', url, error: e });
    return null;
  }
};

export const shouldUseHotelRedirect = (url: string, filters: ReadonlyArray<HotelFilter>): boolean => {
  const parsedUrl = parseUrl(url);

  // If the URL is invalid do not show the hotel redirect
  if (!parsedUrl) {
    return false;
  }

  // Check if any top-level domain filter matches
  const matchingFilter = filters.find(filter =>
    filter.hostname.some(regexStr => {
      const regex = tryCreateRegex(regexStr);
      return !!regex?.test(parsedUrl.hostname || '');
    }),
  );

  // If no matching top-level domain, return false (don't use hotel redirect)
  if (!matchingFilter) {
    return false;
  }

  const matchingLogicResult = matchingFilter.matchingLogic.find(item => {
    const searchRegex = tryCreateRegex(item.search);
    const matches = [];
    if (searchRegex && parsedUrl.search) matches.push(searchRegex.test(parsedUrl.search));

    const pathnameRegex = tryCreateRegex(item.pathname);
    if (pathnameRegex && parsedUrl.pathname) matches.push(pathnameRegex.test(parsedUrl.pathname));

    return matches.length && matches.every(match => match === true);
  })?.result;

  return !!matchingLogicResult;
};

export const useWebsiteInterceptExternalHotelLinks = () => {
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;
  const {
    loading: isInterceptKeyQueryLoading,
    data: interceptKeyData,
    error: hasInterceptKeyQueryError,
  } = useUserPublicConsentKeyForEventQuery({
    variables: {
      consentKey: 'websiteAllowHotelLinkIntercept',
      eventId: eventId!,
    },
    skip: !eventId,
    batchMode: 'fast',
  });
  const { value, payload } = useFeatureValue('websiteInterceptExternalHotelLinksV2');
  const websiteInterceptExternalHotelLinksPayload = payload as WebsiteInterceptExternalHotelLinksPayload;
  const skip = interceptKeyData?.userPublicConsentKeyForEvent?.consentAnswer === false || !!hasInterceptKeyQueryError;

  const getShowIntercept = useCallback(
    (link: string) => {
      if (websiteInterceptExternalHotelLinksPayload) {
        return value === 'on' && !skip && shouldUseHotelRedirect(link, websiteInterceptExternalHotelLinksPayload);
      }
      return false;
    },
    [skip, value, websiteInterceptExternalHotelLinksPayload],
  );

  return { getShowIntercept, isInterceptKeyQueryLoading };
};
